import React from 'react';
import styled from 'styled-components';

import { formatNumber } from 'modules/health/helpers/formatNumber';

const NUM_MARKERS = 60;

const calculateRingMetrics = (score: number, goalScore: number) => {
  const size = 240;
  const center = size / 2;
  const strokeWidth = 10;
  const halfStrokeWidth = strokeWidth / 2;
  const radius = center - halfStrokeWidth;
  const circumference = 2 * Math.PI * radius;

  const safeGoalScore = goalScore > 0 ? goalScore : 1;
  const progress = Math.min(Math.max(score / safeGoalScore, 0), 1);
  const filledArc = circumference * progress;

  const clockRadius = 60;
  const markerLength = 5;

  return {
    size,
    center,
    strokeWidth,
    radius,
    circumference,
    filledArc,
    clockRadius,
    markerLength,
  };
};

interface ScoreRingProps {
  score: number;
  goalScore: number;
  color: string;
}

const ScoreRing = ({ score, goalScore, color }: ScoreRingProps) => {
  const {
    size,
    center,
    strokeWidth,
    radius,
    circumference,
    filledArc,
    clockRadius,
    markerLength,
  } = calculateRingMetrics(score, goalScore);

  const markers = Array.from({ length: NUM_MARKERS }).map((_, i) => {
    const angleDeg = (i * 360) / NUM_MARKERS;
    const key = `marker-${angleDeg}`;

    return (
      <line
        key={key}
        x1={center}
        y1={center - clockRadius}
        x2={center}
        y2={center - clockRadius + markerLength}
        stroke="#fff"
        strokeWidth="1"
        transform={`rotate(${angleDeg} ${center} ${center})`}
      />
    );
  });

  return (
    <RingSection>
      <ShadowContainer>
        <RingSvg viewBox={`0 0 ${size} ${size}`}>
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="#fff"
            strokeWidth="1"
          />

          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={`${filledArc} ${circumference}`}
            transform={`rotate(-90 ${center} ${center})`}
          />

          <circle cx={center} cy={center} r={clockRadius} fill="#ffffff1a" />

          {markers}
        </RingSvg>

        <CenteredColumn>
          <ScoreText>{formatNumber(score)}</ScoreText>
          <Divider />
          <MaxScoreText>{formatNumber(goalScore)}</MaxScoreText>
        </CenteredColumn>
      </ShadowContainer>
    </RingSection>
  );
};

export default ScoreRing;

const RingSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ShadowContainer = styled.div`
  position: relative;
  width: 240px;
  height: 240px;
  display: flex;
  justify-content: center;
  align-items: center;
  filter: drop-shadow(0 5px 10px rgba(0 0 0 / 20%));
`;

const RingSvg = styled.svg`
  position: absolute;
  width: 100%;
  height: 100%;
`;

const CenteredColumn = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const ScoreText = styled.div`
  color: #fff;
  font-weight: 700;
  font-size: 30px;
  line-height: 20px;
  text-align: center;
`;

const Divider = styled.div`
  width: 54px;
  height: 1px;
  background-color: rgba(255 255 255 / 20%);
  margin-top: 11px;
  margin-bottom: 8px;
`;

const MaxScoreText = styled.div`
  color: #fff;
  font-size: 14px;
  line-height: 17px;
`;
